"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FloridaRules = exports.FL_THRESHOLDS = void 0;
const browser_1 = require("encamp-prisma/generated/browser");
const _FEDERAL_1 = require("./_FEDERAL");
const _stateRules_1 = require("./_stateRules");
/**
 * Florida (DEP) accumulation limits, Fla. Admin. Code R. 62-730.160.
 *
 * Subsection (1) adopts 40 CFR part 262 by reference "with the exception of
 * 40 CFR 262.16(c) and 262.10(k)". Everything a container's clock depends on
 * is therefore the federal text, save one thing: 262.16(c) is the SQG's
 * 270-day extension for waste that must travel more than 200 miles, and
 * Florida never took it up. An SQG in Florida stays on the 180 days of
 * adopted 262.16(b) however far the waste travels, and that — the absence of
 * the extension — is the sole place these rules depart from federal.
 *
 * The other exception does not reach a Florida container at all: 40 CFR
 * 262.10(k) lets generators in Massachusetts manage Class A recyclable
 * materials under 310 C.M.R. 30.200 instead, so declining to adopt it changes
 * nothing here. It is called out because reading the exception as a second
 * substantive carve-out would be the natural mistake.
 *
 * Subsections (3) and (4) carry Florida's own inspection-documentation and
 * aisle-space requirements. Neither is a container limit and this engine
 * models neither, so no threshold answers for them.
 *
 * Florida's generator tiers are the federal ones, and RCRAInfo offers it no
 * state status of substance, so like Connecticut and Delaware this class keys
 * on the federal status alone and ignores stateGeneratorStatus.
 *
 * Chapter 62-730 adopts part 273 at 62-730.185 and part 266, subpart P
 * included, at 62-730.181(1). Used oil
 * sits in a chapter of its own: 62-710.210(2) adopts 40 CFR part 279 as
 * revised July 1, 2017, so used oil gets the federal answer.
 *
 * Read against the 9-10-2025 amendment, which re-adopts part 262 as revised
 * July 1, 2024 (plus the July 26, October 11, October 31 and December 11,
 * 2024 Federal Register amendments) and carries the same two exceptions
 * forward from the 4-21-2023 codification. Verified via the Florida
 * Administrative Code mirrors on 2026-09-28.
 */
/** The federal central clock under Florida's rule; every other threshold is federal. */
exports.FL_THRESHOLDS = {
    centralTimeLimit: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.centralTimeLimit,
        source: {
            jurisdiction: "FL",
            citation: "Fla. Admin. Code R. 62-730.160(1) (40 CFR 262.16(c) not adopted)",
        },
    },
};
/** Florida (DEP) accumulation limits, Fla. Admin. Code R. 62-730.160. */
class FloridaRules extends _stateRules_1.StateRules {
    constructor() {
        super(...arguments);
        this.stateStatusDomain = [];
        this.adoptsEpisodicEvents = true;
    }
    facilityThresholds(facility) {
        return _FEDERAL_1.Federal.onSiteThresholds(facility);
    }
    areaThresholds(situation) {
        return _FEDERAL_1.Federal.areaThresholds(situation);
    }
    accumulationCategory(container) {
        // STATE_REGULATED is non-acute pending the waste team's per-state decision.
        return container.classification === browser_1.WasteClassification.STATE_REGULATED
            ? { value: "NON_ACUTE" }
            : _FEDERAL_1.Federal.category(container);
    }
    governingProgram(container) {
        return _FEDERAL_1.Federal.governingProgram(container);
    }
    /**
     * 262.16(c) is the one provision Florida left out, so there is no 200-mile
     * extension to read and the SQG stays on adopted 262.16(b)'s 180 days. Every
     * other status is federal law as adopted.
     */
    centralTimeLimit(situation) {
        if (situation.facility.federalGeneratorStatus === "SQG") {
            return {
                ...exports.FL_THRESHOLDS.centralTimeLimit,
                determination: "KNOWN",
                basis: { facility: { federalGeneratorStatus: "SQG" } },
                value: 180,
            };
        }
        return _FEDERAL_1.Federal.centralTimeLimit(situation);
    }
    /** The subclassification programs run on their own parts, untouched by an exception to part 262. */
    containerThresholds(situation) {
        const program = _FEDERAL_1.Federal.governingProgram(situation.container);
        const episodic = _FEDERAL_1.Federal.episodicWindow(situation.container, program);
        if (program.value !== undefined && _FEDERAL_1.Federal.isSubclassification(program.value)) {
            return [_FEDERAL_1.Federal.subclassificationTimeLimits[program.value], ...episodic];
        }
        switch (situation.area.type) {
            case "CENTRAL":
                return [
                    program.missing === undefined
                        ? this.centralTimeLimit(situation)
                        : _FEDERAL_1.Federal.undeterminedCentralTimeLimit(program.missing),
                    ...episodic,
                ];
            case "SATELLITE": {
                const caps = _FEDERAL_1.Federal.satelliteCaps(situation);
                return [
                    ..._FEDERAL_1.Federal.satelliteMoveWindow(this, _FEDERAL_1.FEDERAL_THRESHOLDS.satelliteToCentralMoveWindow, caps, situation.container),
                    ...episodic,
                ];
            }
            case undefined:
                return [
                    _FEDERAL_1.Federal.undeterminedCentralTimeLimit([...(program.missing ?? []), "area.type"]),
                    ...episodic,
                ];
        }
    }
}
exports.FloridaRules = FloridaRules;
//# sourceMappingURL=FL.js.map